/**
 * Footer reveal.
 *
 * The dark panel grows into place as it scrolls into view: it starts smaller,
 * lower and with rounded corners, and ends full size with square corners.
 * Each text block then slides in from its own side at its own pace. Every
 * value is derived from the scroll position rather than from a one-shot
 * transition, so scrolling away plays the same movement backwards.
 *
 * Directions come from the client's mark-up of the footer:
 * brand and credit line enter from the left, menus, social and payment icons
 * from the right, opening hours and the address from above.
 */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The panel is done by this point, so the empty rectangle stands on screen
     on its own for a while before any text starts to move. */
  const PANEL_END = 0.38;

  /* The background reaches full strength well before the text is done, while
     the blur outlives the panel's movement but clears shortly after it lands.
     The blur curve drops fast and then trails off, so next to nothing is left
     over the text once it starts appearing. */
  const OPACITY_END = 0.6;
  const BLUR_END = 0.7;
  const BLUR_CURVE = 2.2;

  /* Menu columns move as whole blocks: both headings together, then both
     lists together a moment later. */
  // [selector, direction, start (0-1 of the scroll window), length, stagger]
  const ITEMS = [
    ['.footer__brand', 'left', 0.52, 0.34],
    ['.footer__hours-item', 'down', 0.56, 0.22, 0.06],
    ['.footer__details', 'down', 0.72, 0.26],
    ['.footer__links-heading', 'down', 0.52, 0.24],
    ['.footer__links-list', 'down', 0.58, 0.26],
    ['.footer__social', 'right', 0.74, 0.22],
    ['.footer__payment', 'right', 0.8, 0.2],
    ['.footer__credit', 'left', 0.78, 0.22],
  ];

  const clamp = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);
  const easeOut = (value) => 1 - Math.pow(1 - value, 3);

  class FooterReveal {
    constructor(frame) {
      this.frame = frame;
      this.panel = frame.querySelector('.footer');
      if (!this.panel) return;

      this.items = [];
      ITEMS.forEach(([selector, direction, start, length, stagger]) => {
        frame.querySelectorAll(selector).forEach((element, index) => {
          this.items.push({
            element: element,
            direction: direction,
            start: start + (stagger || 0) * index,
            length: length,
          });
        });
      });

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.update = this.update.bind(this);

      this.readSizes();
      frame.classList.add('footer__frame--reveal');
      this.update();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      REDUCED_MOTION.addEventListener('change', this.update);
    }

    /* Phones get a shorter travel so the movement stays calm on small screens. */
    readSizes() {
      const wide = window.matchMedia('(min-width: 750px)').matches;
      /* The frame pads the panel on wide screens; the progress is measured
         from where the panel's top edge actually sits. */
      this.framePadding = parseFloat(window.getComputedStyle(this.frame).paddingTop) || 0;
      this.slide = wide ? 26 : 16;
      this.drop = wide ? 16 : 10;
      this.lift = wide ? 72 : 40;
      this.blur = wide ? 14 : 8;
      this.scale = wide ? 0.97 : 0.98;
    }

    onResize() {
      this.readSizes();
      this.onScroll();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /* 0 when the panel's top edge touches the bottom of the window,
       1 once it has travelled up to 45% of the window height. Pages too short
       to scroll that far finish the reveal at the end of the page instead, so
       the footer is never left half-way.
       The reference is the frame, which never moves: measuring the panel
       itself would feed its own transform back into the progress. */
    progress() {
      const top = this.frame.getBoundingClientRect().top + this.framePadding;
      const from = window.innerHeight;
      const to = window.innerHeight * 0.45;
      const scrolled = window.scrollY || window.pageYOffset;
      const atPageBottom = scrolled >= document.documentElement.scrollHeight - window.innerHeight - 2;

      if (atPageBottom) return 1;
      return clamp((from - top) / Math.max(from - to, 1));
    }

    update() {
      if (REDUCED_MOTION.matches) {
        this.reset();
        return;
      }

      const progress = this.progress();
      const panel = easeOut(clamp(progress / PANEL_END));
      const lift = (1 - panel) * this.lift;

      /* The panel slides up while growing slightly, and its colour keeps
         deepening until the whole reveal is over. The blur softens its edges
         while it travels and is gone once it lands — the filter is dropped
         entirely then, so it costs nothing for the rest of the page. */
      const blur = this.blur * Math.pow(1 - clamp(progress / BLUR_END), BLUR_CURVE);
      const scale = this.scale + (1 - this.scale) * panel;

      this.panel.style.transform = `translate3d(0, ${lift.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      this.panel.style.opacity = (0.1 + 0.9 * easeOut(clamp(progress / OPACITY_END))).toFixed(3);
      this.panel.style.filter = blur > 0.15 ? `blur(${blur.toFixed(2)}px)` : '';

      this.items.forEach((item) => {
        const local = easeOut(clamp((progress - item.start) / item.length));
        const offset = 1 - local;
        let move;

        if (item.direction === 'left') {
          move = `translate3d(${(-this.slide * offset).toFixed(2)}px, 0, 0)`;
        } else if (item.direction === 'right') {
          move = `translate3d(${(this.slide * offset).toFixed(2)}px, 0, 0)`;
        } else {
          move = `translate3d(0, ${(-this.drop * offset).toFixed(2)}px, 0)`;
        }

        item.element.style.transform = move;
        item.element.style.opacity = local.toFixed(3);
      });
    }

    reset() {
      this.panel.style.transform = '';
      this.panel.style.opacity = '';
      this.panel.style.filter = '';
      this.items.forEach((item) => {
        item.element.style.transform = '';
        item.element.style.opacity = '';
      });
    }
  }

  function init() {
    document.querySelectorAll('.footer__frame').forEach((frame) => {
      if (frame.dataset.revealReady) return;
      frame.dataset.revealReady = 'true';
      new FooterReveal(frame);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
